import { AfterContentInit, Directive, ElementRef, HostListener, Input, OnInit, Renderer2 } from '@angular/core';
import { debounce } from '@app/core/utils/debounce';
import { TableConfigService } from '@app/core/services/table-config.service';

/**
 * A structural directive that adds a draggable resize handle to a table column `<th>` element,
 * allowing the user to manually adjust its width by dragging.
 *
 * Usage:
 *   <th appResizeColumn [tableId]="'myTable'" [columnId]="'name'">Name</th>
 */
@Directive({
  selector: '[appResizeColumn]'
})
export class ResizeColumnDirective implements OnInit, AfterContentInit {
  /**
   * Enables or disables the resize behavior. Defaults to true.
   */
  @Input() public appResizeColumn?: boolean | '' = true;

  /**
   * Optional table identifier used for tracking resizable columns.
   */
  @Input() public tableId?: string;

  /**
   * Optional column identifier used for tracking the resized column.
   */
  @Input() public columnId!: string | number | symbol;

  /**
   * Tracks the X-coordinate where resizing started.
   */
  private startX: number = 0;

  /**
   * Stores the column's width at the start of the resize gesture.
   */
  private startWidth: number = 0;

  /**
   * Indicates whether a resize gesture is currently in progress.
   */
  private resizing: boolean = false;

  /**
   * Reference to the resize handle element
   */
  private resizeHandle?: HTMLElement;

  /**
   * Debounce function to handle resize events
   * @private
   */
  private debounce!: (width: number) => void;

  /**
   * Constructor that injects the element reference and renderer.
   *
   * @param el - The element to which this directive is applied.
   * @param renderer - Angular's Renderer2 for safe DOM manipulation.
   * @param tableConfigService - Service to manage table configurations.
   */
  constructor(
    private readonly el: ElementRef,
    private readonly renderer: Renderer2,
    private readonly tableConfigService: TableConfigService,
  ) {}

  /**
   * Angular lifecycle hook that sets up the resize handle if enabled.
   * Adds a span element with class `resize-handle` to the host `<th>` and binds `mousedown`.
   */
  public ngOnInit() {
    this.setupResizeHandle();
  }

  public ngAfterContentInit(): void {
    this.debounce = debounce(this.saveWidth.bind(this), 300);
  }

  private saveWidth(width: number): void {
    if (this.tableId && this.columnId) {
      const tableId = this.tableId;
      this.saveColumnWidth(tableId, this.columnId, width);
    }
  }

  /** Save Settings on Resize **/
  private saveColumnWidth(tableId: string, columnId: string | number | symbol, width: number) {
    this.tableConfigService.saveSetting(tableId, String(columnId), { width });
  }

  /**
   * Public method that can be called to reinitialize the resize handle
   * after drag and drop operations
   */
  public reinitialize() {
    // Remove any existing resize handle
    if (this.resizeHandle && this.el.nativeElement.contains(this.resizeHandle)) {
      this.renderer.removeChild(this.el.nativeElement, this.resizeHandle);
    }

    // Re-setup the resize handle
    this.setupResizeHandle();
  }

  /**
   * Sets up the resize handle on the element
   */
  private setupResizeHandle() {
    if (this.appResizeColumn) {
      this.renderer.setStyle(this.el.nativeElement, 'position', 'relative');

      this.resizeHandle = this.renderer.createElement('div');
      this.renderer.addClass(this.resizeHandle, 'resize-handle');

      // Handle the mousedown event on the handle
      this.renderer.listen(this.resizeHandle, 'mousedown', (event: MouseEvent) => {
        event.preventDefault();
        event.stopPropagation();

        this.startX = event.clientX;
        this.startWidth = this.el.nativeElement.offsetWidth;
        this.resizing = true;
      });

      // Append the handle to the host TH
      this.renderer.appendChild(this.el.nativeElement, this.resizeHandle);
    }
  }

  /**
   * Listens to `mousemove` events on the document and adjusts the column width dynamically.
   *
   * @param event - The mousemove event containing the current cursor position.
   */
  @HostListener('document:mousemove', ['$event'])
  public onMouseMove(event: Event) {
    if (event instanceof MouseEvent) {
      if (!this.resizing) {
        return;
      }

      const offset: number = event.clientX - this.startX;
      const newWidth: number = this.startWidth + offset;

      if (newWidth > 50) {
        this.renderer.setStyle(this.el.nativeElement, 'width', `${newWidth}px`);

        this.debounce(newWidth);
      }
    }
  }

  /**
   * Listens to `mouseup` events on the document to end the resize operation.
   *
   * @param event - The mouseup event (unused).
   */
  @HostListener('document:mouseup', ['$event'])
  public onMouseUp(event: Event) {
    if (event instanceof MouseEvent) {
      this.resizing = false;
    }
  }
}
